/**
 * data/instrumentCatalog/index.ts
 * Main instrument catalog aggregation and exports
 * 
 * Aggregates all asset class-specific instrument catalogs into a unified interface.
 * Replaces the legacy monolithic instrumentCatalog.ts file.
 */

import type { InstrumentCatalogEntry } from '@/types/reference/InstrumentCatalog';

import { FUTURES_INSTRUMENTS } from './futures';
import { FOREX_INSTRUMENTS } from './forex';
import { STOCKS_INSTRUMENTS } from './stocks';
import { OPTIONS_INSTRUMENTS } from './options';
import { CFD_INSTRUMENTS } from './cfd';
import { CRYPTO_INSTRUMENTS } from './crypto';
import { BONDS_INSTRUMENTS } from './bonds';
import { FUNDS_INSTRUMENTS } from './funds';

/**
 * Complete instrument catalog aggregation
 * Contains all tradeable instruments across all asset classes
 */
export const INSTRUMENT_CATALOG: InstrumentCatalogEntry[] = [
  ...FUTURES_INSTRUMENTS,
  ...FOREX_INSTRUMENTS, 
  ...STOCKS_INSTRUMENTS,
  ...OPTIONS_INSTRUMENTS,
  ...CFD_INSTRUMENTS,
  ...CRYPTO_INSTRUMENTS,
  ...BONDS_INSTRUMENTS,
  ...FUNDS_INSTRUMENTS
];

/**
 * Asset class-specific exports for targeted access
 */
export { FUTURES_INSTRUMENTS } from './futures';
export { FOREX_INSTRUMENTS } from './forex';
export { STOCKS_INSTRUMENTS } from './stocks';
export { OPTIONS_INSTRUMENTS } from './options';
export { CFD_INSTRUMENTS } from './cfd';
export { CRYPTO_INSTRUMENTS } from './crypto';
export { BONDS_INSTRUMENTS } from './bonds';
export { FUNDS_INSTRUMENTS } from './funds';

/**
 * Catalog metadata export with helper functions
 */
export { 
  CATALOG_METADATA,
  getVersionKey,
  isVersionNewer,
  formatVersionForDisplay
} from './metadata';

/**
 * Helper functions for systematic catalog access
 */

/**
 * Get all instruments by asset class
 */
export function getInstrumentsByAssetClass(assetClass: string): InstrumentCatalogEntry[] {
  return INSTRUMENT_CATALOG.filter(instrument => instrument.classification.assetClass === assetClass);
}

/**
 * Get futures instruments by market segment
 */
export function getFuturesByCategory(marketSegment: string): InstrumentCatalogEntry[] {
  return FUTURES_INSTRUMENTS.filter(instrument => 
    instrument.classification?.marketSegment === marketSegment
  );
}

/**
 * Get forex instruments by sub-asset class (G10/EM/Other)
 */
export function getForexByCategory(subAssetClass: string): InstrumentCatalogEntry[] {
  return FOREX_INSTRUMENTS.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Get stocks by sub-asset class (stock/etf/adr)
 */
export function getStocksByCategory(subAssetClass: string): InstrumentCatalogEntry[] {
  return STOCKS_INSTRUMENTS.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Get options by sub-asset class (equity-options/etf-options/index-options/futures-options)
 */
export function getOptionsByCategory(subAssetClass: string): InstrumentCatalogEntry[] {
  return OPTIONS_INSTRUMENTS.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Get CFDs by sub-asset class (stock_index/single_stock/energy/precious/industrial)
 */
export function getCFDsByCategory(subAssetClass: string): InstrumentCatalogEntry[] {
  return CFD_INSTRUMENTS.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Get crypto by sub-asset class (layer-1/layer-2/stablecoin/defi/exchange-token/meme-coin/etc.)
 */
export function getCryptoByCategory(subAssetClass: string): InstrumentCatalogEntry[] {
  return CRYPTO_INSTRUMENTS.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Get bonds by sub-asset class (treasury/tips/investment-grade/high-yield/municipal/mbs/aggregate/international/emerging-markets)
 */
export function getBondsByCategory(subAssetClass: string): InstrumentCatalogEntry[] {
  return BONDS_INSTRUMENTS.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Get funds by sub-asset class (large-cap-blend/large-cap-growth/large-cap-value/total-market/dividend/etc.)
 */
export function getFundsByCategory(subAssetClass: string): InstrumentCatalogEntry[] {
  return FUNDS_INSTRUMENTS.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Search instruments by symbol or name
 */
export function searchInstruments(query: string): InstrumentCatalogEntry[] {
  const searchTerm = query.toLowerCase();
  return INSTRUMENT_CATALOG.filter(instrument =>
    instrument.symbol.toLowerCase().includes(searchTerm) ||
    instrument.name.toLowerCase().includes(searchTerm) ||
    instrument.shortName?.toLowerCase().includes(searchTerm)
  );
}

/**
 * Get instrument by ID
 */
export function getInstrumentById(id: string): InstrumentCatalogEntry | undefined {
  return INSTRUMENT_CATALOG.find(instrument => instrument.id === id);
}

/**
 * Get instruments by market segment
 */
export function getInstrumentsByMarketSegment(marketSegment: string): InstrumentCatalogEntry[] {
  return INSTRUMENT_CATALOG.filter(instrument =>
    instrument.classification?.marketSegment === marketSegment
  );
}

/**
 * Get instruments by sub-asset class
 */
export function getInstrumentsBySubAssetClass(subAssetClass: string): InstrumentCatalogEntry[] {
  return INSTRUMENT_CATALOG.filter(instrument =>
    instrument.classification?.subAssetClass === subAssetClass
  );
}

/**
 * Get available market segments
 */
export function getAvailableMarketSegments(): string[] {
  const segments = new Set<string>();
  INSTRUMENT_CATALOG.forEach(instrument => {
    if (instrument.classification?.marketSegment) {
      segments.add(instrument.classification.marketSegment);
    }
  });
  return Array.from(segments).sort();
}

/**
 * Get available sub-asset classes
 */
export function getAvailableSubAssetClasses(): string[] {
  const subAssetClasses = new Set<string>();
  INSTRUMENT_CATALOG.forEach(instrument => {
    if (instrument.classification?.subAssetClass) {
      subAssetClasses.add(instrument.classification.subAssetClass);
    }
  });
  return Array.from(subAssetClasses).sort();
}

/**
 * Catalog statistics
 */
export const CATALOG_STATS = {
  total: INSTRUMENT_CATALOG.length,
  futures: FUTURES_INSTRUMENTS.length,
  forex: FOREX_INSTRUMENTS.length,
  stocks: STOCKS_INSTRUMENTS.length,
  options: OPTIONS_INSTRUMENTS.length,
  cfd: CFD_INSTRUMENTS.length,
  crypto: CRYPTO_INSTRUMENTS.length,
  bonds: BONDS_INSTRUMENTS.length,
  funds: FUNDS_INSTRUMENTS.length
} as const;